import { useState, useEffect } from 'react';
import i18next from 'i18next';
import { Breadcrumb, Col, Container, Row } from 'react-bootstrap';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useApiClient } from '../../hooks/useApiClient';
import OpportunityFunnel, {
	FunnelStep,
} from '../../components/Opportunities/OpportunityFunnel/OpportunityFunnel';
import {
	AccountEntitySearch,
	OpportunityEntity,
} from '../../api/models/AccountEntity';
import {
	ConfirmSellVehicleRequestDTO,
	OptionDTO,
	VehicleStateEnum,
	VehicleEntity,
} from '../../api';
import { toast } from 'react-toastify';
import { OpportunityStageEnum, RoleEnum } from '../../api/models/Enums';
import CardPlaceholder from '../../components/CardPlaceholder/CardPlaceholder';
import { useAuth } from '../../hooks/useAuth';
import { useTranslation } from 'react-i18next';
import { getPrevPage } from '../../utils/opportunity';
import useSalesforceStore from '../../store/SalesforceStore';
import _ from 'lodash';
import { OpportunityFunnelV2 } from 'src/components/Opportunities/OpportunityFunnelV2/OpportunityFunnelV2';

const CreateOpportunityPage = () => {
	const { userRole } = useAuth();
	const navigate = useNavigate();
	const location = useLocation();
	const params = new URLSearchParams(location.search);
	const action = params.get('action') ?? ''; // create_option | confirm_sell | update_vehicle | send_docs
	const { apiClient, handleApiResponse, handleApi } = useApiClient();
	const { id } = useParams<{ id: string }>();
	const opportunityId = location.pathname.includes('new') ? 'new' : id;
	const breadCrumbFrom = params.get('from') ?? 'account';
	const [loading, setLoading] = useState(true);
	const [nextState, setNextState] = useState<OpportunityStageEnum>();
	const [account, setAccount] = useState<AccountEntitySearch | undefined>();
	const [vehicle, setVehicle] = useState<VehicleEntity | undefined>();
	const [opportunity, setOpportunity] = useState<
		OpportunityEntity | undefined
	>();
	const [canChange, setCanChange] = useState<FunnelStep[]>([]);
	const [mustComplete, setMustComplete] = useState<FunnelStep[]>([]);
	const [pageTitle, setPageTitle] = useState<string>('');
	const { setAccountSalesforce } = useSalesforceStore();
	const saveLabels = {
		confirm_sell: 'Conferma vendita',
	};
	const { t } = useTranslation();
	const opportunityNewStage = params.get('opportunityNewStage') || null;

	const afterSubmitFn = (id) => {
		setLoading(true);
		afterSubmitAction(id).finally(() => {
			setLoading(false);
		});
	};

	const handlePrevPage = () => {
		return getPrevPage(breadCrumbFrom, {
			accountId: account?.Id,
			opportunityId,
			vehicle,
		});
	};

	const afterSubmitAction = async (id: string) => {
		const opRes = await handleApi(
			apiClient.salesforceOpportunityController.getOpportunity(id)
		);

		if (!opRes.data.length) {
			toast.error(
				i18next.t('internal_error_missing_data_for_option_confirmation')
			);
			return;
		}

		const opportunity: OpportunityEntity = opRes.data[0];

		if (
			action === 'create_option' ||
			(opportunityId === 'new' &&
				opportunity.StageName === OpportunityStageEnum.credit_score_check)
		) {
			try {
				const accountRes =
					account ||
					(
						await handleApi(
							apiClient.salesforceAccountController.getAccount(
								opportunity.AccountId
							)
						)
					).data[0];

				if (!accountRes) {
					toast.error(i18next.t('missing_data_to_option_vehicle'));
					return;
				}

				if (opportunity.ID_Vehicle_HMP__c) {
					const optionRequest: OptionDTO = {
						cliente: accountRes?.Name,
						opportunityId: id,
						vehicleId: opportunity.ID_Vehicle_HMP__c,
						ragione_sociale:
							accountRes.Codice_Fiscale__c || accountRes.Partita_IVA__c || '',
						noleggiatore: opportunity.Noleggiatore__c || '',
						document: [],
					};

					await handleApi(
						apiClient.vehicleActionsController.postVehiclesActions(
							optionRequest
						)
					);
				}

				navigate(handlePrevPage());
			} catch (err) {
				toast.error(i18next.t('unable_to_option_vehicle'));
			}
		} else if (action === 'confirm_sell') {
			if (
				![
					RoleEnum.MASTER,
					RoleEnum.BACKOFFICE,
					RoleEnum.ADMIN,
					RoleEnum.AREA_MANAGER,
				].includes(userRole)
			) {
				toast.error(
					i18next.t('user_unauthorized_to_confirm_sell_vehicle', { userRole })
				);
				return;
			}

			try {
				const accountRes =
					account ||
					(
						await handleApi(
							apiClient.salesforceAccountController.getAccount(
								opportunity.AccountId
							)
						)
					).data[0];
				if (!accountRes) {
					toast.error(i18next.t('missing_data_to_confirm_vehicle_sale'));
					return;
				}

				if (opportunity.ID_Vehicle_HMP__c) {
					const closeRequest: ConfirmSellVehicleRequestDTO = {
						cliente: accountRes?.Name,
						opportunityId: id,
						pratica_credito: id,
						vehicleId: opportunity.ID_Vehicle_HMP__c,
						noleggiatore: opportunity.Noleggiatore__c || '',
						ragione_sociale:
							accountRes.Codice_Fiscale__c || accountRes.Partita_IVA__c || '',
						targa: opportunity.Targa__c,
					};

					await handleApi(
						apiClient.vehicleActionsController.postVehiclesActionsConfirmSell(
							closeRequest
						)
					);
					// await handleApiResponse(apiClient.vehicleController.putVehicles(closeRequest?.vehicleId, {targa: opportunity.Targa__c}))
				}

				navigate(handlePrevPage());
			} catch (err) {
				console.error(err);
				toast.error(i18next.t('unable_to_option_vehicle'));
			}
		} else if (
			action === 'update_vehicle' &&
			opportunityNewStage === OpportunityStageEnum.credit_score_check
		) {
			try {
				const accountRes =
					account ||
					(
						await handleApi(
							apiClient.salesforceAccountController.getAccount(
								opportunity.AccountId
							)
						)
					).data[0];

				if (!accountRes) {
					toast.error(i18next.t('missing_data_to_option_vehicle'));
					return;
				}

				if (opportunity.ID_Vehicle_HMP__c) {
					const optionRequest: OptionDTO = {
						cliente: accountRes?.Name,
						opportunityId: id,
						vehicleId: opportunity.ID_Vehicle_HMP__c,
						ragione_sociale:
							accountRes.Codice_Fiscale__c || accountRes.Partita_IVA__c || '',
						noleggiatore: opportunity.Noleggiatore__c || '',
						document: [],
					};

					await handleApi(
						apiClient.vehicleActionsController.postVehiclesActions(
							optionRequest
						)
					);
				}

				navigate(handlePrevPage());
			} catch (err) {
				toast.error(i18next.t('unable_to_option_vehicle'));
			}
		} else if (
			action === 'update_vehicle' &&
			opportunityNewStage === OpportunityStageEnum.closed_won
		) {
			if (
				![
					RoleEnum.MASTER,
					RoleEnum.BACKOFFICE,
					RoleEnum.ADMIN,
					RoleEnum.AREA_MANAGER,
				].includes(userRole)
			) {
				toast.error(
					i18next.t('user_unauthorized_to_confirm_sell_vehicle', { userRole })
				);
				return;
			}
			try {
				const accountRes =
					account ||
					(
						await handleApi(
							apiClient.salesforceAccountController.getAccount(
								opportunity.AccountId
							)
						)
					).data[0];
				if (!accountRes) {
					toast.error(i18next.t('missing_data_to_confirm_vehicle_sale'));
					return;
				}

				if (opportunity.ID_Vehicle_HMP__c) {
					const closeRequest: ConfirmSellVehicleRequestDTO = {
						cliente: accountRes?.Name,
						opportunityId: id,
						pratica_credito: id,
						vehicleId: opportunity.ID_Vehicle_HMP__c,
						noleggiatore: opportunity.Noleggiatore__c || '',
						ragione_sociale:
							accountRes.Codice_Fiscale__c || accountRes.Partita_IVA__c || '',
						targa: opportunity.Targa__c,
					};

					await handleApi(
						apiClient.vehicleActionsController.postVehiclesActionsConfirmSell(
							closeRequest
						)
					);
				}

				navigate(handlePrevPage());
			} catch (err) {
				console.error(err);
				toast.error(i18next.t('unable_to_option_vehicle'));
			}
		} else {
			navigate(handlePrevPage());
		}
	};

	const loadData = async () => {
		let nextState = OpportunityStageEnum.waiting_sign;

		// Setting title
		if (opportunityId === 'new') {
			if (action === 'confirm_sell') {
				setPageTitle(i18next.t('confirm_sale'));
			} else if (action === 'create_option') {
				setPageTitle(i18next.t('insert_option'));
			} else {
				setPageTitle(i18next.t('create_opportunity'));
			}
		} else {
			if (action === 'confirm_sell') {
				setPageTitle(i18next.t('confirm_sale'));
			} else if (action === 'create_option') {
				setPageTitle(i18next.t('insert_option'));
			} else {
				setPageTitle(i18next.t('modify_opportunity'));
			}
		}

		// Setting must complete
		if (opportunityId === 'new') {
			if (['create_option', 'confirm_sell'].includes(action!)) {
				setMustComplete(['account', 'vehicle', 'opportunity', 'upload_docs']);
			} else if (action === 'send_docs') {
				setMustComplete(['account', 'vehicle', 'opportunity', 'send_docs']);
			} else {
				setMustComplete([
					'account',
					'vehicle',
					'opportunity',
					'send_or_upload_docs',
				]);
			}
		} else {
			if (action === 'confirm_sell') {
				setMustComplete(['account', 'vehicle']);
			} else if (action === 'update_vehicle') {
				setMustComplete(['vehicle']);
			} else if (vehicle) {
				setMustComplete(['vehicle']);
			}
		}

		// Setting next opportunity state
		if (action === 'create_option') {
			nextState = OpportunityStageEnum.credit_score_check;
		} else if (action === 'confirm_sell') {
			nextState = OpportunityStageEnum.closed_won;
		} else if (action === 'update_vehicle') {
			if (opportunityNewStage === OpportunityStageEnum.credit_score_check) {
				nextState = OpportunityStageEnum.credit_score_check;
			} else if (opportunityNewStage === OpportunityStageEnum.closed_won) {
				nextState = OpportunityStageEnum.closed_won;
			}
		}

		setNextState(nextState);

		// NEW OPPORTUNITY
		if (opportunityId === 'new') {
			// FETCH ACCOUNT if PARAMS has account_id
			if (params.has('account_id')) {
				setCanChange(['vehicle', 'send_docs', 'upload_docs', 'opportunity']);
				const accountRes = await handleApiResponse(
					apiClient.salesforceAccountController.getAccount(
						params.get('account_id') || ''
					)
				);
				setAccount(accountRes.data[0]);
				setAccountSalesforce(accountRes.data[0]);
			}

			// FETCH VEHICLE IF PARAMS has vehicle_id
			else if (params.has('vehicle_id')) {
				if (action === 'send_docs') {
					setCanChange(['account', 'send_docs', 'opportunity']);
				} else {
					setCanChange(['account', 'upload_docs', 'opportunity']);
				}

				const vehicleRes = await handleApiResponse(
					apiClient.vehicleController.getVehicles1(
						params.get('vehicle_id') || ''
					)
				);
				setVehicle(vehicleRes);
			}
		} else if (opportunityId) {
			// EXISTING OPPORTUNITY
			// get opportunity from opportunityId
			const opportunityRes = await handleApiResponse(
				apiClient.salesforceOpportunityController.getOpportunity(opportunityId)
			);

			// if there are opportunities available
			if (opportunityRes.data.length > 0) {
				const op: OpportunityEntity = opportunityRes.data[0];

				// if the opportunity is closed, redirect to the opportunity detail page
				if (
					[
						OpportunityStageEnum.closed_won,
						OpportunityStageEnum.closed_by_credit_score_check,
						OpportunityStageEnum.closed_by_customer,
					].includes(op.StageName)
				) {
					navigate(`/opportunities/${op.Id}`);
					return;
				}

				setOpportunity(op);

				// get account and vehicle data HMP if there is a HMP vehicle available
				const [accountRes, vehicleRes] = await Promise.all([
					apiClient.salesforceAccountController.getAccount(op.AccountId),
					op.ID_Vehicle_HMP__c
						? apiClient.vehicleController.getVehicles1(op.ID_Vehicle_HMP__c)
						: Promise.resolve(),
				]);

				if (vehicleRes) {
					// if there is a stoto_corrente and is either VENDUTO or OPZIONATO
					if (
						vehicleRes.stato_corrente?.type &&
						[
							VehicleStateEnum.OPZIONATO,
							VehicleStateEnum.VENDUTO,
							VehicleStateEnum.OSCURATO,
						].includes(vehicleRes.stato_corrente!.type)
					) {
						if (
							vehicleRes.stato_corrente?.type === VehicleStateEnum.OPZIONATO &&
							vehicleRes.stato_corrente?.opportunityId === opportunityId
						) {
							setCanChange(['upload_docs']);
							setVehicle(vehicleRes);
						} else {
							// se il veicolo non è piu disponibile, offrire la possibilità di modificarlo
							if (action === 'update_vehicle') {
								setCanChange(['vehicle']);
							} else {
								setCanChange(['upload_docs', 'vehicle']);
							}

							setVehicle(undefined);
						}
					} else {
						setCanChange(['upload_docs']);
						setVehicle(vehicleRes);
					}
				} else {
					setCanChange(['upload_docs']);
					setVehicle(undefined);
				}

				setAccount(accountRes.data[0]);
				setAccountSalesforce(accountRes.data[0]);
			} else {
				toast.error(i18next.t('opportunity_not_found'));
			}
		}

		setLoading(false);
	};

	useEffect(() => {
		loadData();
	}, []);

	const renderOpportunityFunnel = () => {
		if (opportunityId === 'new' && breadCrumbFrom === 'account') {
			return (
				<div>
					{/* NEW OPPORTUNITY */}
					<OpportunityFunnelV2
						mustComplete={mustComplete}
						account={account!}
						nextState={nextState}
						saveLabel={action ? saveLabels[action] : i18next.t('create')}
						onQuit={() => window.location.replace(handlePrevPage())}
						afterSubmitFn={afterSubmitFn}
					/>
				</div>
			);
		}

		if (opportunityId === 'new') {
			return (
				<div>
					{/* NEW OPPORTUNITY */}
					<OpportunityFunnel
						startFrom={'account'}
						mustComplete={mustComplete}
						canChange={canChange}
						vehicle={vehicle}
						account={account}
						nextState={nextState}
						saveLabel={action ? saveLabels[action] : i18next.t('create')}
						onQuit={() => window.location.replace(handlePrevPage())}
						afterSubmitFn={afterSubmitFn}
					/>
				</div>
			);
		}

		return (
			<div>
				{/* EXISTING OPPORTUNITY */}
				<OpportunityFunnel
					opportunity={opportunity}
					startFrom={action === 'update_vehicle' ? 'vehicle' : 'upload_docs'}
					mustComplete={mustComplete}
					canChange={canChange}
					vehicle={vehicle}
					account={account}
					nextState={nextState}
					saveLabel={action ? saveLabels[action] : i18next.t('save')}
					onQuit={() => window.location.replace(handlePrevPage())}
					afterSubmitFn={afterSubmitFn}
				/>
			</div>
		);
	};

	return (
		<Container fluid>
			<Breadcrumb>
				{breadCrumbFrom === 'account' && (
					<>
						<Breadcrumb.Item href={'/my-accounts'}>
							{t('account_list')}
						</Breadcrumb.Item>
						<Breadcrumb.Item
							href={'/my-accounts/' + (account ? account.Id : '')}
						>
							{account ? account.Name : '...'}
						</Breadcrumb.Item>
					</>
				)}

				{breadCrumbFrom === 'vehicle' && (
					<>
						<Breadcrumb.Item href="/available-vehicles">
							{t('vehicles_list')}
						</Breadcrumb.Item>
						<Breadcrumb.Item href={'/details/' + (vehicle ? vehicle.id : '')}>
							{vehicle
								? `${vehicle.dati_veicolo?.marca} ${vehicle.dati_veicolo?.modello}`
								: '...'}
						</Breadcrumb.Item>
					</>
				)}

				{breadCrumbFrom === 'opportunity' && (
					<>
						<Breadcrumb.Item href={'/my-accounts'}>
							{t('account_list')}
						</Breadcrumb.Item>
						<Breadcrumb.Item
							href={'/my-accounts/' + (account ? account.Id : '')}
						>
							{account ? account.Name : '...'}
						</Breadcrumb.Item>
						<Breadcrumb.Item href={'/opportunities/' + opportunityId}>
							{opportunity ? opportunity.Name : '...'}
						</Breadcrumb.Item>
					</>
				)}
				<Breadcrumb.Item active>{pageTitle}</Breadcrumb.Item>
			</Breadcrumb>
			<Row>
				<Col md={6} className="display-6">
					{pageTitle}
				</Col>
			</Row>

			{!loading ? (
				<div className="mt-4">{renderOpportunityFunnel()}</div>
			) : (
				<div className="mt-3">
					<CardPlaceholder />
				</div>
			)}
		</Container>
	);
};

export default CreateOpportunityPage;
